<template>
  <div class="videowall" :class="{ 'videowall--chromeless': chromeHidden }">
    <GmapMap
      ref="mapRef"
      :center="mapCenter"
      :zoom="12"
      map-type-id="roadmap"
      :options="mapOptions"
      class="videowall__map"
      @click="closeInfo"
    >
      <GmapMarker
        v-for="occ in visibleOccurrences"
        :key="'occ-' + occ.id"
        :position="{ lat: occ.latitude, lng: occ.longitude }"
        :title="occ.protocol"
        :icon="occurrenceIcon(occ)"
        :label="occurrenceLabel(occ)"
        @click="openOccurrence(occ)"
      />
      <FleetVehicleMarker
        v-for="pos in visibleFleet"
        :key="'fleet-' + pos.identifier_code"
        :position="pos"
        :title="fleetMarkerTitle(pos)"
        :z-index="1000"
        @click="openTeam(pos)"
      />
      <GmapInfoWindow
        :options="{ maxWidth: 360 }"
        :position="infoPos"
        :opened="!!infoPos"
        @closeclick="closeInfo"
      >
        <div v-if="selectedOccurrence" class="vw-info">
          <div class="vw-info__title">
            <strong>#{{ selectedOccurrence.protocol }}</strong>
            <span class="vw-info__ago">{{ timeAgo(selectedOccurrence.created_at) }}</span>
          </div>
          <div v-if="selectedOccurrence.dispatch_type">
            {{ selectedOccurrence.discipline }} · {{ selectedOccurrence.dispatch_type }}
            <span v-if="selectedOccurrence.dispatch_reason"> · {{ selectedOccurrence.dispatch_reason }}</span>
          </div>
          <div v-if="selectedOccurrence.address">
            {{ selectedOccurrence.address }}<span v-if="selectedOccurrence.number">, {{ selectedOccurrence.number }}</span>
            <span v-if="selectedOccurrence.neighborhood"> — {{ selectedOccurrence.neighborhood }}</span>
          </div>
          <div v-if="selectedOccurrence.attending_vehicles && selectedOccurrence.attending_vehicles.length" class="vw-info__teams">
            <span
              v-for="(v, i) in selectedOccurrence.attending_vehicles"
              :key="i"
              class="vw-info__team"
            >{{ v.team_name || v.vehicle_name }}<em v-if="v.status_code"> {{ v.status_code }}</em></span>
          </div>
          <div v-if="selectedOccurrence.complaint" class="vw-info__complaint">"{{ selectedOccurrence.complaint }}"</div>
        </div>
        <div v-else-if="selectedTeam" class="vw-info">
          <div class="vw-info__title"><strong>{{ selectedTeam.team ? selectedTeam.team.name : selectedTeam.identifier_code }}</strong></div>
          <div>{{ fleetStateLabel(selectedTeam) }} · {{ fleetAgeLabel(selectedTeam) }}</div>
        </div>
      </GmapInfoWindow>
    </GmapMap>

    <!-- Barra superior: contexto + ações -->
    <header class="videowall__bar">
      <div class="videowall__brand">
        <feather-icon icon="MonitorIcon" size="18" />
        <strong>{{ nodeName }}</strong>
        <span class="videowall__count">{{ $t('videowall.occurrencesCount', { count: visibleOccurrences.length }) }}</span>
        <span v-if="showTeams" class="videowall__count">{{ $t('videowall.teamsCount', { count: visibleFleet.length }) }}</span>
      </div>
      <div class="videowall__bar-right">
        <span class="videowall__live" :class="{ 'videowall__live--off': !live }" :title="live ? $t('videowall.live') : $t('videowall.reconnecting')">
          <span class="videowall__live-dot" />
          {{ live ? $t('videowall.live') : $t('videowall.reconnecting') }}
        </span>
        <span class="videowall__clock">{{ clockText }}</span>
        <button type="button" class="vw-btn" :title="$t('videowall.fit')" @click="fitToVisible">
          <feather-icon icon="CrosshairIcon" size="16" />
        </button>
        <button type="button" class="vw-btn" :title="$t('videowall.filters')" @click="filtersOpen = !filtersOpen">
          <feather-icon icon="SlidersIcon" size="16" />
        </button>
        <button type="button" class="vw-btn" :title="isFullscreen ? $t('videowall.exitFullscreen') : $t('videowall.fullscreen')" @click="toggleFullscreen">
          <feather-icon :icon="isFullscreen ? 'Minimize2Icon' : 'Maximize2Icon'" size="16" />
        </button>
      </div>
    </header>

    <!-- Filtros -->
    <aside v-if="filtersOpen" class="videowall__filters">
      <div class="vw-filter__title">
        {{ $t('videowall.searchTitle') }}
        <span v-if="selectedIds.length" class="vw-badge">{{ $t('videowall.selectedCount', { count: selectedOccurrences.length }) }}</span>
      </div>

      <div v-if="selectedOccurrences.length" class="vw-chips vw-chips--selected">
        <button
          v-for="o in selectedOccurrences"
          :key="'sel-' + o.id"
          type="button"
          class="vw-chip vw-chip--on"
          :title="occurrenceLine(o)"
          @click="focusOccurrence(o)"
        >#{{ o.protocol }} <span class="vw-chip__x" @click.stop="toggleSelected(o.id)">×</span></button>
      </div>

      <input
        v-model="search"
        type="search"
        class="vw-search"
        :placeholder="$t('videowall.searchPlaceholder')"
        @keydown.enter.prevent="addPastedProtocols"
        @keydown.esc="search = ''"
      >
      <div class="vw-hint">{{ $t('videowall.pasteHint') }}</div>

      <div class="vw-list-actions">
        <button type="button" class="vw-link" :disabled="!searchResults.length" @click="selectResults">
          {{ $t('videowall.selectResults', { count: searchResults.length }) }}
        </button>
        <button v-if="selectedIds.length" type="button" class="vw-link" @click="selectedIds = []">
          {{ $t('videowall.clearSelection') }}
        </button>
      </div>

      <ul class="vw-results">
        <li v-if="!searchResults.length" class="vw-muted">{{ $t('videowall.noResults') }}</li>
        <li
          v-for="o in searchResults"
          :key="o.id"
          class="vw-result"
          :class="{ 'vw-result--on': isSelected(o.id) }"
          @click="toggleSelected(o.id)"
        >
          <input type="checkbox" :checked="isSelected(o.id)" tabindex="-1" readonly class="vw-check-box">
          <div class="vw-result__text">
            <strong>#{{ o.protocol }}</strong>
            <span>{{ occurrenceLine(o) }}</span>
          </div>
          <button type="button" class="vw-result__go" :title="$t('videowall.goTo')" @click.stop="focusOccurrence(o)">
            <feather-icon icon="CrosshairIcon" size="14" />
          </button>
        </li>
      </ul>

      <div class="vw-filter__title">
        {{ $t('videowall.teamsTitle') }}
        <span v-if="selectedTeamCodes.length" class="vw-badge">{{ $t('videowall.selectedCount', { count: selectedTeams.length }) }}</span>
      </div>

      <div v-if="selectedTeams.length" class="vw-chips vw-chips--selected">
        <button
          v-for="t in selectedTeams"
          :key="'selteam-' + t.code"
          type="button"
          class="vw-chip vw-chip--on"
          @click="focusTeam(t.position)"
        >{{ t.name }} <span class="vw-chip__x" @click.stop="toggleTeam(t.code)">×</span></button>
      </div>

      <input
        v-model="teamSearch"
        type="search"
        class="vw-search"
        :placeholder="$t('videowall.teamsSearchPlaceholder')"
        @keydown.esc="teamSearch = ''"
      >

      <div class="vw-list-actions">
        <button type="button" class="vw-link" :disabled="!teamResults.length" @click="selectTeamResults">
          {{ $t('videowall.selectTeams', { count: teamResults.length }) }}
        </button>
        <button v-if="selectedTeamCodes.length" type="button" class="vw-link" @click="selectedTeamCodes = []">
          {{ $t('videowall.clearSelection') }}
        </button>
      </div>

      <ul class="vw-results vw-results--teams">
        <li v-if="!teamResults.length" class="vw-muted">{{ $t('videowall.noTeams') }}</li>
        <li
          v-for="t in teamResults"
          :key="'team-' + t.code"
          class="vw-result"
          :class="{ 'vw-result--on': isTeamSelected(t.code) }"
          @click="toggleTeam(t.code)"
        >
          <input type="checkbox" :checked="isTeamSelected(t.code)" tabindex="-1" readonly class="vw-check-box">
          <div class="vw-result__text">
            <strong>{{ t.name }}</strong>
            <span>{{ t.code }} · {{ t.state }}</span>
          </div>
          <button type="button" class="vw-result__go" :title="$t('videowall.goToTeam')" @click.stop="focusTeam(t.position)">
            <feather-icon icon="CrosshairIcon" size="14" />
          </button>
        </li>
      </ul>

      <div class="vw-filter__title">{{ $t('videowall.layers') }}</div>
      <label class="vw-check"><input v-model="showOccurrences" type="checkbox"> {{ $t('videowall.showOccurrences') }}</label>
      <label class="vw-check"><input v-model="showTeams" type="checkbox"> {{ $t('videowall.showTeams') }}</label>

      <div class="vw-filter__title">{{ $t('videowall.pinLabel') }}</div>
      <div class="vw-chips">
        <button
          v-for="option in labelOptions"
          :key="option.field"
          type="button"
          class="vw-chip"
          :class="{ 'vw-chip--on': labelField === option.field }"
          @click="setLabelField(option.field)"
        >{{ option.label }}</button>
      </div>

      <div class="vw-filter__title">{{ $t('videowall.disciplines') }}</div>
      <div v-if="!disciplineOptions.length" class="vw-muted">{{ $t('videowall.none') }}</div>
      <div class="vw-chips">
        <button
          v-for="d in disciplineOptions"
          :key="d.id"
          type="button"
          class="vw-chip"
          :class="{ 'vw-chip--on': isDisciplineOn(d.id) }"
          @click="toggleDiscipline(d.id)"
        >{{ d.name }} <span class="vw-chip__n">{{ d.count }}</span></button>
      </div>

      <template v-if="nodeOptions.length > 1">
        <div class="vw-filter__title">{{ $t('videowall.nodes') }}</div>
        <div class="vw-chips">
          <button
            v-for="n in nodeOptions"
            :key="n.id"
            type="button"
            class="vw-chip"
            :class="{ 'vw-chip--on': isNodeOn(n.id) }"
            @click="toggleNode(n.id)"
          >{{ n.name }} <span class="vw-chip__n">{{ n.count }}</span></button>
        </div>
      </template>

      <button type="button" class="vw-link" @click="clearFilters">{{ $t('videowall.clearFilters') }}</button>
    </aside>

    <div v-if="loadFailed && !occurrences.length" class="videowall__error">{{ $t('videowall.loadError') }}</div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import FleetPositionsMixin from '@/mixins/FleetPositionsMixin'
import OccurrenceFeedMixin from '@/mixins/OccurrenceFeedMixin'
import RealtimeResyncMixin from '@/mixins/RealtimeResyncMixin'
import FleetVehicleMarker from '@/components/FleetVehicleMarker.vue'
import { occurrencePin } from '@/utils/pinMarker'

const PIN_PADRAO = 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'
const FILTERS_KEY = 'videowall.filters'
// Rede de segurança: eventos em tempo real podem se perder (rede da TV, queda do websocket).
const SAFETY_RELOAD_MS = 60000
// Sem ninguém mexer na tela por este tempo, o mapa volta a enquadrar tudo o que está visível.
const IDLE_RESET_MS = 60000
const INTERACTION_EVENTS = ['mousedown', 'mousemove', 'wheel', 'keydown', 'touchstart']

export default {
  name: 'Videowall',

  components: { FleetVehicleMarker },

  mixins: [FleetPositionsMixin, OccurrenceFeedMixin, RealtimeResyncMixin],

  data() {
    return {
      occurrences: [],
      loadFailed: false,
      mapCenter: { lat: -27.5954, lng: -48.548 },
      selectedOccurrence: null,
      selectedTeam: null,
      infoPos: null,
      filtersOpen: false,
      showOccurrences: true,
      showTeams: true,
      labelField: null,
      search: '',
      selectedIds: [],
      teamSearch: '',
      selectedTeamCodes: [],
      lastInteractionAt: Date.now(),
      needsViewReset: false,
      idleTimer: null,
      disciplineIds: [],
      nodeIds: [],
      pinsQuebrados: [],
      pinsVerificados: [],
      live: true,
      isFullscreen: false,
      chromeHidden: false,
      clockText: '',
      clockTimer: null,
      reloadTimer: null,
      fitPending: true,
      wakeLock: null,
      connection: null,
      onConnectionState: null,
    }
  },

  computed: {
    nodeName() {
      const node = this.$store.state.selected_node
      return node && node.name ? node.name : ''
    },
    labelOptions() {
      return [
        { field: 'protocol', label: this.$t('occurrence.mapMenu.protocol') },
        { field: 'requester_name', label: this.$t('occurrence.mapMenu.requesterName') },
        { field: 'dispatch_type', label: this.$t('occurrence.mapMenu.type') },
      ]
    },
    mapOptions() {
      return {
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: 'greedy',
        clickableIcons: false,
        fullscreenControl: false,
      }
    },
    // Disciplinas e nós existentes nas ocorrências abertas, com a contagem, para os filtros.
    disciplineOptions() {
      return this.countBy(this.occurrences, 'discipline_id', 'discipline')
    },
    nodeOptions() {
      return this.countBy(this.occurrences, 'node_id', 'node_name')
    },
    // Ocorrências que passam nos filtros de disciplina e nó (a busca é aplicada por cima).
    filteredOccurrences() {
      return this.occurrences.filter(o => (
        (!this.disciplineIds.length || this.disciplineIds.includes(o.discipline_id))
        && (!this.nodeIds.length || this.nodeIds.includes(o.node_id))
      ))
    },
    // Lista do painel: tudo o que passa nos filtros, afunilado pelo texto da busca (a busca só ajuda a achar).
    searchResults() {
      const term = this.normalize(this.search.trim())
      if (!term) return this.filteredOccurrences

      return this.filteredOccurrences.filter(o => this.normalize(this.searchableText(o)).includes(term))
    },
    // Escolhidas que ainda existem (uma finalizada some da seleção sozinha).
    selectedOccurrences() {
      return this.filteredOccurrences.filter(o => this.selectedIds.includes(o.id))
    },
    // Com ocorrências selecionadas o mapa mostra só elas; sem seleção, mostra tudo o que passa nos filtros.
    visibleOccurrences() {
      if (!this.showOccurrences) return []

      return this.selectedIds.length ? this.selectedOccurrences : this.filteredOccurrences
    },
    // Todas as equipes com posição, no formato da lista do painel.
    teamOptions() {
      return this.fleetPositionsOnMap
        .map(p => ({
          code: p.identifier_code,
          name: p.team ? p.team.name : p.identifier_code,
          state: this.fleetStateLabel(p),
          position: p,
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    teamResults() {
      const term = this.normalize(this.teamSearch.trim())
      if (!term) return this.teamOptions

      return this.teamOptions.filter(t => this.normalize(`${t.name} ${t.code} ${t.state}`).includes(term))
    },
    selectedTeams() {
      return this.teamOptions.filter(t => this.selectedTeamCodes.includes(t.code))
    },
    // Com equipes selecionadas o mapa mostra só elas; sem seleção, todas.
    visibleFleet() {
      if (!this.showTeams) return []
      if (!this.selectedTeamCodes.length) return this.fleetPositionsOnMap

      return this.fleetPositionsOnMap.filter(p => this.selectedTeamCodes.includes(p.identifier_code))
    },
  },

  watch: {
    disciplineIds() {
      this.onFiltersChanged()
    },
    nodeIds() {
      this.onFiltersChanged()
    },
    selectedIds() {
      this.closeInfo()
      this.$nextTick(this.fitToVisible)
    },
    selectedTeamCodes() {
      this.closeInfo()
      this.$nextTick(this.fitToVisible)
    },
    showOccurrences() {
      this.persistFilters()
    },
    showTeams() {
      this.persistFilters()
    },
  },

  created() {
    this.restoreFilters()
    this.tickClock()
    this.clockTimer = setInterval(this.tickClock, 1000)
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
    document.addEventListener('visibilitychange', this.requestWakeLock)
    this.watchInteraction()

    // As habilidades chegam do servidor depois de a página abrir (a rota pode ser
    // aberta direto na TV): só decide depois que o usuário carregou.
    this.stopWaitingUser = this.$watch(
      () => this.$store.state.user.id,
      id => {
        if (id) this.startWhenAllowed()
      },
      { immediate: true },
    )
  },

  beforeDestroy() {
    if (this.stopWaitingUser) this.stopWaitingUser()
    clearInterval(this.clockTimer)
    clearInterval(this.reloadTimer)
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
    document.removeEventListener('visibilitychange', this.requestWakeLock)
    this.unwatchInteraction()
    this.unwatchRealtimeResync()
    this.unwatchConnection()
    this.releaseWakeLock()
  },

  methods: {
    // Só abre para quem tem a habilidade no papel (a Home só mostra o botão para eles).
    startWhenAllowed() {
      if (this.started) return

      if (!this.$hasAbility('videowall.ver')) {
        this.$router.replace('/')
        return
      }

      this.started = true
      this.loadOccurrences()
      this.loadFleetPositions()
      this.watchOccurrenceFeed()
      this.watchFleetPositions()
      this.watchRealtimeResync(() => this.reloadAll())
      this.watchConnection()
      this.requestWakeLock()
      this.reloadTimer = setInterval(() => this.loadOccurrences(false), SAFETY_RELOAD_MS)
    },

    tickClock() {
      this.clockText = moment().format('HH:mm:ss')
    },

    countBy(list, idKey, nameKey) {
      const map = {}
      list.forEach(o => {
        const id = o[idKey]
        if (id === null || id === undefined) return
        if (!map[id]) map[id] = { id, name: o[nameKey] || String(id), count: 0 }
        map[id].count += 1
      })

      return Object.values(map).sort((a, b) => a.name.localeCompare(b.name))
    },

    // ── Dados ──────────────────────────────────────────────────────────
    loadOccurrences(fit = false) {
      return axios
        .get(`${this.$store.state.api}occurrences/map-data`)
        .then(res => {
          this.occurrences = res.data.data || []
          this.loadFailed = false
          if (fit || this.fitPending) {
            this.fitPending = false
            this.$nextTick(this.fitToVisible)
          }
        })
        .catch(() => {
          // Mantém o que já estava no mapa: a TV não pode ficar vazia por um erro passageiro.
          this.loadFailed = true
        })
    },

    reloadAll() {
      this.loadOccurrences()
      this.loadFleetPositions()
    },

    // Chamado pelo OccurrenceFeedMixin a cada ocorrência criada ou que muda de etapa.
    applyOccurrenceFeed() {
      this.scheduleRealtimeReload(() => {
        this.clearOccurrenceFeed()
        this.loadOccurrences()
      })
    },

    // ── Mapa ───────────────────────────────────────────────────────────
    async fitToVisible() {
      if (!this.$refs.mapRef) return

      const points = [
        ...this.visibleOccurrences.map(o => ({ lat: o.latitude, lng: o.longitude })),
        ...this.visibleFleet.map(p => ({ lat: p.lat, lng: p.lng })),
      ]
      if (!points.length) return

      try {
        const map = await this.$refs.mapRef.$mapPromise
        const bounds = new window.google.maps.LatLngBounds()
        points.forEach(p => bounds.extend(p))
        map.fitBounds(bounds, 80)
        // Um único ponto não deve encostar o zoom no máximo.
        if (points.length === 1) map.setZoom(15)
      } catch (e) {
        // Mapa ainda carregando: o próximo ajuste resolve.
      }
    },

    occurrenceIcon(occ) {
      const pin = occurrencePin(occ, u => this.usablePin(u)) || { url: PIN_PADRAO, size: null }
      const px = pin.size || 44

      return {
        url: pin.url,
        scaledSize: { width: px, height: px },
        anchor: { x: px / 2, y: px },
        labelOrigin: { x: px / 2, y: -14 },
      }
    },

    occurrenceLabel(occ) {
      if (!this.labelField) return null
      const text = occ[this.labelField]
      if (!text) return null

      return {
        text: String(text),
        className: 'vw-pin-label',
        color: '#1a3c73',
        fontSize: '12px',
        fontWeight: '700',
      }
    },

    // Clicar de novo na opção ativa tira o rótulo.
    setLabelField(field) {
      this.labelField = this.labelField === field ? null : field
      this.persistFilters()
    },

    // Ícone que não carrega faz o Google Maps não desenhar o marcador: a URL que falhou é descartada.
    usablePin(url) {
      if (!url || this.pinsQuebrados.includes(url)) return null
      if (!this.pinsVerificados.includes(url)) {
        this.pinsVerificados.push(url)
        const img = new Image()
        img.onerror = () => {
          if (!this.pinsQuebrados.includes(url)) this.pinsQuebrados.push(url)
        }
        img.src = url
      }

      return url
    },

    openOccurrence(occ) {
      this.selectedTeam = null
      this.selectedOccurrence = occ
      this.infoPos = { lat: occ.latitude, lng: occ.longitude }
    },

    openTeam(pos) {
      this.selectedOccurrence = null
      this.selectedTeam = pos
      this.infoPos = { lat: pos.lat, lng: pos.lng }
    },

    closeInfo() {
      this.infoPos = null
      this.selectedOccurrence = null
      this.selectedTeam = null
    },

    timeAgo(dateStr) {
      if (!dateStr) return ''
      const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
      if (diff < 60) return this.$t('occurrence.timeAgo.now')
      if (diff < 3600) return this.$t('occurrence.timeAgo.minutes', { m: Math.floor(diff / 60) })
      if (diff < 86400) return this.$t('occurrence.timeAgo.hours', { h: Math.floor(diff / 3600) })
      const d = Math.floor(diff / 86400)

      return this.$t(d > 1 ? 'occurrence.timeAgo.days' : 'occurrence.timeAgo.day', { d })
    },

    // ── Volta ao enquadramento geral quando ninguém mexe na tela ─────────
    watchInteraction() {
      INTERACTION_EVENTS.forEach(name => window.addEventListener(name, this.onInteraction, { passive: true, capture: true }))
      this.idleTimer = setInterval(this.checkIdle, 5000)
    },

    unwatchInteraction() {
      INTERACTION_EVENTS.forEach(name => window.removeEventListener(name, this.onInteraction, { capture: true }))
      clearInterval(this.idleTimer)
    },

    // Só interação de verdade (as animações do mapa e do próprio script não contam).
    onInteraction(event) {
      if (!event.isTrusted) return
      this.lastInteractionAt = Date.now()
      this.needsViewReset = true
    },

    // Depois de um tempo sem ninguém mexer, fecha o cartão aberto e reenquadra tudo o que está visível.
    checkIdle() {
      if (!this.needsViewReset || Date.now() - this.lastInteractionAt < IDLE_RESET_MS) return

      this.needsViewReset = false
      this.closeInfo()
      this.fitToVisible()
    },

    // ── Equipes ────────────────────────────────────────────────────────
    isTeamSelected(code) {
      return this.selectedTeamCodes.includes(code)
    },

    toggleTeam(code) {
      this.selectedTeamCodes = this.toggled(this.selectedTeamCodes, code)
    },

    selectTeamResults() {
      this.selectedTeamCodes = [...new Set([...this.selectedTeamCodes, ...this.teamResults.map(t => t.code)])]
    },

    async focusTeam(position) {
      this.openTeam(position)
      try {
        const map = await this.$refs.mapRef.$mapPromise
        map.panTo({ lat: position.lat, lng: position.lng })
        map.setZoom(16)
      } catch (e) {
        // mapa ainda carregando
      }
    },

    // ── Busca ──────────────────────────────────────────────────────────
    normalize(value) {
      return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    },

    searchableText(o) {
      return [o.protocol, o.address, o.number, o.neighborhood, o.city, o.discipline, o.dispatch_type,
        o.dispatch_reason, o.complaint, o.requester_name, o.node_name].filter(Boolean).join(' ')
    },

    occurrenceLine(o) {
      const where = [o.address, o.neighborhood].filter(Boolean).join(' — ')
      const what = [o.dispatch_type, o.dispatch_reason].filter(Boolean).join(' · ')

      return [what, where].filter(Boolean).join(' · ')
    },

    isSelected(id) {
      return this.selectedIds.includes(id)
    },

    toggleSelected(id) {
      this.selectedIds = this.toggled(this.selectedIds, id)
    },

    // Seleciona de uma vez tudo o que a busca mostra agora.
    selectResults() {
      const ids = this.searchResults.map(o => o.id)
      this.selectedIds = [...new Set([...this.selectedIds, ...ids])]
    },

    // Cola vários protocolos (separados por vírgula, espaço ou quebra de linha) e tecla Enter: seleciona os que existem.
    addPastedProtocols() {
      const tokens = this.search.split(/[\s,;]+/).map(t => t.replace(/\D/g, '')).filter(t => t.length >= 6)
      if (!tokens.length) return

      const found = this.occurrences.filter(o => tokens.includes(String(o.protocol)))
      if (!found.length) return

      this.selectedIds = [...new Set([...this.selectedIds, ...found.map(o => o.id)])]
      this.search = ''
    },

    // Centraliza a ocorrência escolhida na lista e abre o cartão dela.
    async focusOccurrence(occ) {
      this.openOccurrence(occ)
      try {
        const map = await this.$refs.mapRef.$mapPromise
        map.panTo({ lat: occ.latitude, lng: occ.longitude })
        map.setZoom(16)
      } catch (e) {
        // mapa ainda carregando
      }
    },

    // ── Filtros ────────────────────────────────────────────────────────
    isDisciplineOn(id) {
      return this.disciplineIds.includes(id)
    },
    isNodeOn(id) {
      return this.nodeIds.includes(id)
    },
    toggleDiscipline(id) {
      this.disciplineIds = this.toggled(this.disciplineIds, id)
    },
    toggleNode(id) {
      this.nodeIds = this.toggled(this.nodeIds, id)
    },
    toggled(list, id) {
      return list.includes(id) ? list.filter(x => x !== id) : [...list, id]
    },
    clearFilters() {
      this.disciplineIds = []
      this.nodeIds = []
      this.search = ''
      this.selectedIds = []
      this.teamSearch = ''
      this.selectedTeamCodes = []
    },
    onFiltersChanged() {
      this.persistFilters()
      this.closeInfo()
      this.$nextTick(this.fitToVisible)
    },
    persistFilters() {
      try {
        localStorage.setItem(FILTERS_KEY, JSON.stringify({
          disciplineIds: this.disciplineIds,
          nodeIds: this.nodeIds,
          showOccurrences: this.showOccurrences,
          showTeams: this.showTeams,
          labelField: this.labelField,
        }))
      } catch (e) {
        // sem localStorage: os filtros valem só nesta abertura
      }
    },
    restoreFilters() {
      try {
        const saved = JSON.parse(localStorage.getItem(FILTERS_KEY) || 'null')
        if (!saved) return
        this.disciplineIds = Array.isArray(saved.disciplineIds) ? saved.disciplineIds : []
        this.nodeIds = Array.isArray(saved.nodeIds) ? saved.nodeIds : []
        this.showOccurrences = saved.showOccurrences !== false
        this.showTeams = saved.showTeams !== false
        this.labelField = this.labelOptions.some(o => o.field === saved.labelField) ? saved.labelField : null
      } catch (e) {
        // filtro salvo corrompido: começa limpo
      }
    },

    // ── Tela cheia, conexão e "tela não apaga" ────────────────────────
    toggleFullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen()
        return
      }
      const el = document.documentElement
      if (el.requestFullscreen) el.requestFullscreen().catch(() => {})
    },

    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement
    },

    watchConnection() {
      const connector = window.Echo && window.Echo.connector
      const connection = connector && connector.pusher && connector.pusher.connection
      if (!connection) return

      this.connection = connection
      this.live = connection.state === 'connected'
      this.onConnectionState = states => {
        this.live = states.current === 'connected'
      }
      connection.bind('state_change', this.onConnectionState)
    },

    unwatchConnection() {
      if (this.connection && this.onConnectionState) this.connection.unbind('state_change', this.onConnectionState)
      this.connection = null
      this.onConnectionState = null
    },

    async requestWakeLock() {
      if (document.visibilityState !== 'visible' || !navigator.wakeLock || this.wakeLock) return

      try {
        this.wakeLock = await navigator.wakeLock.request('screen')
        this.wakeLock.addEventListener('release', () => {
          this.wakeLock = null
        })
      } catch (e) {
        // navegador recusou (economia de energia): a TV continua funcionando
      }
    },

    releaseWakeLock() {
      if (this.wakeLock) this.wakeLock.release().catch(() => {})
      this.wakeLock = null
    },
  },
}
</script>

<style scoped>
.videowall {
  position: fixed;
  inset: 0;
  background: #0f1420;
}

.videowall__map {
  width: 100%;
  height: 100%;
}

.videowall__bar {
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 14px;
  border-radius: 12px;
  background: rgba(15, 20, 32, 0.82);
  color: #fff;
  backdrop-filter: blur(6px);
  pointer-events: none;
}

.videowall__bar > * {
  pointer-events: auto;
}

.videowall__brand,
.videowall__bar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.videowall__brand strong {
  font-size: 1.15rem;
}

.videowall__count {
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  font-size: 0.9rem;
}

.videowall__clock {
  font-variant-numeric: tabular-nums;
  font-size: 1.25rem;
  font-weight: 600;
}

.videowall__live {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
}

.videowall__live-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #28c76f;
}

.videowall__live--off .videowall__live-dot {
  background: #ea5455;
}

.vw-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 8px;
  background: transparent;
  color: #fff;
  cursor: pointer;
}

.vw-btn:hover {
  background: rgba(255, 255, 255, 0.15);
}

.videowall__filters {
  position: absolute;
  top: 70px;
  right: 12px;
  z-index: 5;
  width: 320px;
  max-height: calc(100% - 90px);
  overflow-y: auto;
  padding: 14px;
  border-radius: 12px;
  background: rgba(15, 20, 32, 0.9);
  color: #fff;
}

.vw-filter__title {
  margin: 12px 0 6px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.7;
}

.vw-filter__title:first-child {
  margin-top: 0;
}

.vw-check {
  color: #fff;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0;
  cursor: pointer;
}

.vw-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.vw-chip {
  padding: 4px 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 999px;
  background: transparent;
  color: #fff;
  font-size: 0.85rem;
  cursor: pointer;
}

.vw-chip--on {
  border-color: #3fc6d8;
  background: #3fc6d8;
  color: #0b1117;
  font-weight: 600;
}

.vw-chip__n {
  margin-left: 4px;
  opacity: 0.75;
}

.vw-badge {
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #3fc6d8;
  color: #0b1117;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.vw-chips--selected {
  margin-bottom: 8px;
}

.vw-chip__x {
  margin-left: 4px;
  font-weight: 700;
}

.vw-hint {
  margin: 4px 0 6px;
  font-size: 0.72rem;
  opacity: 0.6;
}

.vw-list-actions {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.vw-list-actions .vw-link {
  margin-top: 0;
  font-size: 0.85rem;
}

.vw-list-actions .vw-link:disabled {
  opacity: 0.4;
  cursor: default;
}

.vw-search {
  width: 100%;
  padding: 7px 10px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.vw-search::placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.vw-results--teams {
  max-height: 180px;
}

.vw-results {
  max-height: 260px;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
}

.vw-result {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 0.85rem;
  cursor: pointer;
}

.vw-result:hover {
  background: rgba(255, 255, 255, 0.12);
}

.vw-check-box {
  /* Só indicador: o clique é da linha inteira. Se a caixa recebesse o clique, o navegador desfaria a marcação. */
  pointer-events: none;
}

.vw-result--on {
  background: rgba(63, 198, 216, 0.18);
}

.vw-result__text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.vw-result__go {
  display: inline-flex;
  padding: 4px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #fff;
  opacity: 0.6;
  cursor: pointer;
}

.vw-result__go:hover {
  background: rgba(255, 255, 255, 0.15);
  opacity: 1;
}

.vw-result__text span {
  opacity: 0.75;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vw-muted {
  opacity: 0.6;
  font-size: 0.85rem;
}

.vw-link {
  margin-top: 14px;
  padding: 0;
  border: 0;
  background: none;
  color: #3fc6d8;
  cursor: pointer;
}

.videowall__error {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 18px;
  border-radius: 10px;
  background: rgba(234, 84, 85, 0.92);
  color: #fff;
  z-index: 5;
}

.vw-info {
  min-width: 240px;
  font-size: 13px;
  line-height: 1.5;
  color: #222;
}

.vw-info__title {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
  font-size: 14px;
}

.vw-info__ago {
  color: #777;
  font-size: 12px;
}

.vw-info__teams {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
}

.vw-info__team {
  padding: 1px 8px;
  border-radius: 10px;
  background: #e7f1ff;
  color: #1a58a8;
  font-size: 12px;
  font-weight: 600;
}

.vw-info__complaint {
  margin-top: 6px;
  padding: 5px 7px;
  border-radius: 4px;
  background: #f4f5f7;
  font-style: italic;
}
</style>

<style>
/* Rótulo do pin no mapa — não escopado: o Google Maps o renderiza fora da árvore do componente. */
.vw-pin-label {
  position: relative;
  display: inline-block;
  background: #ffffff;
  border: 1.5px solid #1a73e8;
  border-radius: 7px;
  padding: 3px 10px;
  box-shadow: 0 2px 6px rgba(26, 115, 232, 0.3);
  white-space: nowrap;
  letter-spacing: 0.15px;
}
.vw-pin-label::before {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -7px;
  transform: translateX(-50%);
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #1a73e8;
}
.vw-pin-label::after {
  content: "";
  position: absolute;
  left: 50%;
  bottom: -5px;
  transform: translateX(-50%);
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #fff;
}
</style>
